import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import getCountries from "../utils/getCountries";

interface SelectOption {
  label: string;
  value: string;
}

interface Region {
  name: string;
  shortCode?: string;
}

interface CountryData {
  label: string;
  value: string;
  regions?: Region[];
  countryName?: string;
  countryShortCode?: string;
}

interface UseCountryRegionFormOptionsProps {
  country?: string;
  region?: string;
}

interface UseCountryRegionFormOptionsReturn {
  countryOptions: CountryData[];
  country: CountryData | undefined;
  regionOptions: SelectOption[] | undefined;
  region: SelectOption | null | undefined;
}

const useCountryRegionFormOptions = ({
  country,
  region,
}: UseCountryRegionFormOptionsProps): UseCountryRegionFormOptionsReturn => {
  const { i18n } = useTranslation();

  const countryOptions = useMemo(() => {
    return getCountries(i18n.language).map(({ name, shortCode, ...other }) => ({
      ...other,
      label: String(name),
      value: shortCode,
    }));
  }, [i18n.language]);

  const currCountry = useMemo(
    () => countryOptions.find(({ value }: CountryData) => value === country),
    [country, countryOptions]
  );

  const regionOptions = useMemo(
    () => currCountry?.regions?.map(({ name }: Region) => ({ label: name, value: name })),
    [currCountry]
  );

  const currRegion = useMemo(
    () => (region ? regionOptions?.find(({ value }: SelectOption) => value === region) : null),
    [regionOptions, region]
  );

  return {
    countryOptions,
    country: currCountry,
    regionOptions,
    region: currRegion,
  };
};

export default useCountryRegionFormOptions;
